import React, { useState, useEffect } from 'react';
import { db, functions } from '../../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import BrowseListings from './BrowseListings';
import SellItems from './SellItems';
import MyActivity from './MyActivity';
import { useToast } from '../ToastContext';
import { PgHeader, SubTabs } from '../ui';

const MKT_TABS = [
  { id: 'browse', label: '🛒 Browse' },
  { id: 'sell',   label: '💵 Sell Items' },
  { id: 'mybids', label: '📋 My Activity' },
];
function MarketplacePage({ user, userData, onUserDataChange, onNotificationCreated }) {
  const isS2 = true;
  const toast = useToast();
  const [selectedTab, setSelectedTab] = useState("browse"); // "browse", "sell", "mybids"
  const [listings, setListings] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  // Load marketplace data
  useEffect(() => {
    loadMarketplaceData(true); // Pass true for initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadMarketplaceData(isInitialLoad = false) {
    // Only show loading spinner on initial load, not on refreshes after purchases
    if (isInitialLoad) {
      setLoading(true);
    }
    try {
      // Load all marketplace listings
      const listingsSnapshot = await getDocs(
        query(collection(db, "marketplace"), orderBy("timestamp", "desc"))
      );
      const allListings = listingsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Check for expired auctions and process them
      await processExpiredAuctions(allListings);

      // Reload after processing expired auctions
      const refreshedSnapshot = await getDocs(
        query(collection(db, "marketplace"), orderBy("timestamp", "desc"))
      );
      const refreshedListings = refreshedSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setListings(refreshedListings);

      // Filter user's listings if logged in
      if (user) {
        const userListings = refreshedListings.filter(l => l.sellerId === user.uid);
        setMyListings(userListings);

        // Load user's bids
        const bidsSnapshot = await getDocs(
          query(collection(db, "marketplaceBids"), orderBy("timestamp", "desc"))
        );
        const allBids = bidsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        const userBids = allBids.filter(b => b.bidderId === user.uid);
        setMyBids(userBids);
      }
    } catch (error) {
      console.error("Error loading marketplace:", error);
    }
    // Only update loading state if this was an initial load
    if (isInitialLoad) {
      setLoading(false);
    }
  }

  // Process expired auctions
  async function processExpiredAuctions(listings) {
    const now = Date.now();
    const expiredAuctions = listings.filter(listing =>
      listing.auctionMode &&
      listing.auctionEndTime &&
      listing.auctionEndTime < now &&
      listing.status === "active"
    );

    for (const auction of expiredAuctions) {
      try {
        await completeAuction(auction);
      } catch (error) {
        console.error("Error completing auction:", auction.id, error);
      }
    }
  }

  // Complete an auction — delegates entirely to server-side CF
  async function completeAuction(auction) {
    console.log("Completing auction:", auction.id);
    const completeAuctionFn = httpsCallable(functions, 'completeAuction');
    await completeAuctionFn({ listingId: auction.id });
    if (onNotificationCreated) {
      await onNotificationCreated();
    }
  }

  async function handleListItem(item, price, auctionMode, currencyType, auctionDurationHours) {
    if (!user || !userData) {
      toast.error("Please log in to list items!");
      return;
    }

    // Prevent spam-clicking
    if (isProcessing) return;

    if (price < 0.01) {
      toast.warn("Price must be at least 0.01 tokens!");
      return;
    }

    if (price > 1000000) {
      toast.warn("Price cannot exceed 1,000,000 tokens!");
      return;
    }

    setIsProcessing(true);

    // Optimistic: show success immediately and switch to browse tab
    const tokenIcon = currencyType === "crewmate" ? "🔵" : "🔴";
    toast.success(`Item listed for ${price} ${tokenIcon}!`);

    const createListingFn = httpsCallable(functions, 'createListing');
    createListingFn({ item, price, auctionMode, currencyType, auctionDurationHours }).then(() => {
      onUserDataChange();
      loadMarketplaceData();
    }).catch((error) => {
      console.error("Error listing item:", error);
      toast.error(error.message || "Failed to list item. Please try again.");
      loadMarketplaceData();
      onUserDataChange();
    }).finally(() => {
      setIsProcessing(false);
    });
  }

  async function handleBuyNow(listing) {
    if (!user || !userData) {
      toast.error("Please log in to buy items!");
      return;
    }

    // Prevent spam-clicking and double-purchasing
    if (isProcessing) return;

    if (!listing || !listing.item) {
      toast.error("Invalid listing!");
      return;
    }

    if (listing.sellerId === user.uid) {
      toast.warn("You can't buy your own listing!");
      return;
    }

    setIsProcessing(true);

    // Optimistic: remove listing from browse, show success
    setListings(prev => prev.filter(l => l.id !== listing.id));
    toast.success("Purchase successful!");

    const purchaseFn = httpsCallable(functions, 'purchaseListing');
    purchaseFn({ listingId: listing.id }).then(() => {
      if (onNotificationCreated) onNotificationCreated();
      onUserDataChange();
      loadMarketplaceData();
    }).catch((error) => {
      console.error("Error purchasing item:", error);
      toast.error(error.message || "Failed to purchase. Please try again.");
      loadMarketplaceData();
      onUserDataChange();
    }).finally(() => {
      setIsProcessing(false);
    });
  }

  async function handlePlaceBid(listing, bidAmount, bidCurrency) {
    if (!user || !userData) {
      toast.error("Please log in to place bids!");
      return;
    }

    // Prevent spam-clicking and double-bidding
    if (isProcessing) return;

    // Enforce currency type matches listing
    if (bidCurrency !== listing.currencyType) {
      const correctIcon = listing.currencyType === "crewmate" ? "🔵" : "🔴";
      toast.warn(`This auction only accepts ${listing.currencyType} tokens ${correctIcon}!`);
      return;
    }

    if (listing.sellerId === user.uid) {
      toast.warn("You can't bid on your own listing!");
      return;
    }

    // Determine minimum bid: either current bid + 0.01, or listing price (starting bid)
    const minimumBid = listing.currentBid > 0 ? parseFloat((listing.currentBid + 0.01).toFixed(2)) : listing.price;

    if (bidAmount < minimumBid) {
      const tokenIcon = listing.currencyType === "crewmate" ? "🔵" : "🔴";
      if (listing.currentBid > 0) {
        toast.warn(`Bid must be higher than current bid of ${listing.currentBid} ${tokenIcon}! Minimum bid: ${minimumBid} ${tokenIcon}`);
      } else {
        toast.warn(`Bid must be at least the starting price of ${listing.price} ${tokenIcon}!`);
      }
      return;
    }

    setIsProcessing(true);

    // Optimistic: update listing's current bid in local state
    setListings(prev => prev.map(l => l.id === listing.id ? { ...l, currentBid: bidAmount, highestBidder: user.uid } : l));
    toast.success("Bid placed! Your tokens are held in escrow.");

    const bidFn = httpsCallable(functions, 'placeBid');
    bidFn({ listingId: listing.id, bidAmount, bidCurrency }).then(() => {
      onUserDataChange();
      loadMarketplaceData();
    }).catch((error) => {
      console.error("Error placing bid:", error);
      toast.error(error.message || "Failed to place bid. Please try again.");
      loadMarketplaceData();
      onUserDataChange();
    }).finally(() => {
      setIsProcessing(false);
    });
  }

  async function handleCancelListing(listingId) {
    // Optimistic: remove from state immediately for instant feedback
    setMyListings(prev => prev.filter(l => l.id !== listingId));
    setListings(prev => prev.filter(l => l.id !== listingId));
    try {
      const cancelListingFn = httpsCallable(functions, 'cancelListing');
      await cancelListingFn({ listingId });
      await onUserDataChange();
      await loadMarketplaceData();
    } catch (error) {
      console.error("Error cancelling listing:", error);
      // Roll back optimistic removal on error
      await loadMarketplaceData();
      toast.error(error.message || "Failed to cancel listing.");
    }
  }

  if (!user) {
    return (
      <div className="v2-page">
        <div style={{ textAlign: 'center', padding: '80px 20px', color: 'var(--text-2)' }}>
          <div style={{ fontSize: 44, marginBottom: 14, opacity: 0.35 }}>🔒</div>
          <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 14, letterSpacing: '0.12em', color: 'var(--text)' }}>
            LOGIN REQUIRED
          </div>
          <div style={{ marginTop: 8, fontSize: 13 }}>Please log in to access the marketplace</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="v2-page">
        <div style={{ textAlign: 'center', padding: '80px 20px', color: 'var(--text-2)' }}>
          <div style={{ fontSize: 44, marginBottom: 14 }}>🪙</div>
          <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 12, letterSpacing: '0.12em' }}>
            LOADING MARKETPLACE…
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="v2-page">
      <PgHeader
        title="Marketplace"
        sub="Buy, sell, and bid on cosmetic items"
        action={
          <button
            type="button"
            className="v2-btn"
            onClick={loadMarketplaceData}
            disabled={loading}
          >
            {loading ? '🔄 Loading…' : '🔄 Refresh'}
          </button>
        }
      />

      <SubTabs tabs={MKT_TABS} active={selectedTab} onChange={setSelectedTab} />

      {selectedTab === "browse" && (
        <BrowseListings
          listings={listings.filter(l => l.status === "active" && l.sellerId !== user.uid)}
          userData={userData}
          onBuyNow={handleBuyNow}
          onPlaceBid={handlePlaceBid}
          isS2={isS2}
        />
      )}

      {selectedTab === "sell" && (
        <SellItems
          inventory={userData.lootboxInventory || []}
          onListItem={handleListItem}
          myListings={myListings}
          onCancelListing={handleCancelListing}
          isS2={isS2}
        />
      )}

      {selectedTab === "mybids" && (
        <MyActivity
          myListings={myListings}
          myBids={myBids}
          listings={listings}
          onCancelListing={handleCancelListing}
        />
      )}

    </div>
  );
}

export default MarketplacePage;
